import { reportTranslation } from 'app/area/company-report/constants';
import { errorSafe } from 'app/infrastructure/redux';
import { createDescriptionSelector } from 'app/infrastructure/translate/index';
import { isValidListOfEmails } from 'app/infrastructure/validation/email';
import { ApplicationState } from 'app/types-store';
import { ActionDispatching, FavouriteDefinition } from 'app/types-shared';
import { ValidationLevel, ValidationProps } from 'ui';
import * as actions from './action-types';
import { api, endpoint } from './api';
import { SuccessNotification, ErrorNotification } from 'app/infrastructure/notifications/user-messages';
import { AutoCloseDuration, toastFactory } from 'app/infrastructure/notifications/toast-factory';
import { createUploader } from 'app/infrastructure/remote-data/http';
import { downloadFile } from 'app/infrastructure/platform/file';
import { CONTENT_DISPOSITION_HEADER } from 'app/infrastructure/remote-data/constants';
import { toast } from 'react-toastify';
import { mapScheduleDefinitionToWire } from 'app/area/company-report/components/schedule-helpers';
import { ScheduleDefinition } from 'app/area/company-report/types-store';

export const actionCreators = {

  loadMetadata: (companyReportId: number): ActionDispatching<any, any> =>
    errorSafe(async (dispatch: any) => {

      dispatch(new actions.RequestMetadata());

      const [
        metadata,
        usePredefDate
      ] = await Promise.all([
        api.loadMetadata(companyReportId),
        api.loadUsePredefDate(companyReportId)
      ]);

      dispatch(new actions.ReceiveMetadata(metadata, usePredefDate));
    }),

  saveReport: (definition: FavouriteDefinition, successMessage: () => JSX.Element): ActionDispatching<any, any> =>
    async (dispatch: any, getState: () => ApplicationState) => {

      const reportState = getState().companyReport;
      const translate = createDescriptionSelector(reportState.texts);

      try {
        const toastMessage = toastFactory({ autoClose: AutoCloseDuration.Longer });
        dispatch(new SuccessNotification(translate(reportTranslation.report_started)));

        dispatch(new actions.RequestSendReport());

        await api.sendReport(definition);

        toastMessage.dispatch('', toast.TYPE.SUCCESS);
        toastMessage.success(successMessage);

        dispatch(new actions.ReceiveReport());

      } catch (err) {
        dispatch(new actions.FailedToSendReport());

        const errorMessage = translate(reportTranslation.report_not_generated);
        dispatch(new ErrorNotification(errorMessage));
      }
    },

  sendReport: (emails: string, definition: FavouriteDefinition, successMessage: () => JSX.Element): ActionDispatching<any, any> =>
    async (dispatch: any, getState: () => ApplicationState) => {

      const reportState = getState().companyReport;
      const translate = createDescriptionSelector(reportState.texts);
      try {
        const toastMessage = toastFactory({ autoClose: AutoCloseDuration.Longer });

        if (isValidListOfEmails(emails)) {
          dispatch(new SuccessNotification(translate(reportTranslation.report_sent_by_email)));

          dispatch(new actions.RequestSendReport());

          const updatedDefinition = {...definition, ...({ emailSubscribers: emails })};

          await api.sendReport(updatedDefinition);

          toastMessage.dispatch('', toast.TYPE.SUCCESS);
          toastMessage.success(successMessage);

          dispatch(new actions.ReceiveReport());

        } else {
          const validationProps: ValidationProps = {
            level: ValidationLevel.error,
            messages: [{ message: translate(reportTranslation.must_be_filled_out), level: ValidationLevel.error }]
          };
          dispatch(new actions.SetEmailValue(emails, validationProps));
        }
      } catch (err) {
        dispatch(new actions.FailedToSendReport());

        const errorMessage = translate(reportTranslation.report_not_generated);
        dispatch(new ErrorNotification(errorMessage));
      }
    },

  setEmail: (value: string, validation?: ValidationProps): ActionDispatching<any, any> =>
    errorSafe(async (dispatch: any, getState: any) => {

      dispatch(new actions.SetEmailValue(value?.trim(), validation));
    }),

  clearForm: (): ActionDispatching<any, any> =>
    errorSafe(async (dispatch: any, getState: any) => {

      dispatch(new actions.ClearForm());
    }),

  validateReportFilters: (): ActionDispatching<any, any> =>
    errorSafe(async (dispatch: any, getState: any) => {

      const reportState = getState().companyReport;
      const translate = createDescriptionSelector(reportState.texts);
      const errorMessage = translate(reportTranslation.some_fields_are_filled_out_incorrectly);
      dispatch(new ErrorNotification(errorMessage));
      dispatch(new actions.ValidateReportFilters());
    }),

  updateTemplate: (reportId: number, file: File, onClose: () => void): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {
      try {
        const reportState = getState().companyReport;
        const translate = createDescriptionSelector(reportState.texts);
        const xhr = createUploader(endpoint.specialReportTemplate(reportId), 'PUT');
        const data = new FormData();
        data.append('TemplatePackage', file, file.name);

        xhr.onload = () => {
          if (xhr.status === 200) {
            onClose();
            const successMessage = translate(reportTranslation.upload_success);
            dispatch(new SuccessNotification(successMessage));
          } else {
            const errorMessage = translate(reportTranslation.problem_uploading_file);
            dispatch(new ErrorNotification(errorMessage));
          }
        };

        xhr.onerror = () => {
          dispatch(new ErrorNotification(translate(reportTranslation.reload_file)));
        };

        xhr.send(data);
      }
      catch (err) {
        dispatch(new ErrorNotification(err.errorMessage));
      }
    },

  downloadTemplate: (reportId: number | string, fileName: string) =>
    async (dispatch: any, getState: any) => {
      const reportState = getState().companyReport;
      const translate = createDescriptionSelector(reportState.texts);
      try {
        const resource = await api.downloadTemplate(reportId);
        const response = await resource.blob();
        const blobName = response?.headers?.get(CONTENT_DISPOSITION_HEADER)?.split('filename=')[1] || fileName;
        downloadFile(response.blob, blobName);
      } catch (err) {
        const errorMessage = translate(reportTranslation.file_not_found);
        dispatch(new ErrorNotification(errorMessage));
      }
    },

  deleteScheduleDefinition: (favouriteId: number, scheduleDefinitionId: number): ActionDispatching<any, any> =>
    async (dispatch: any, getState: () => ApplicationState) => {
      
      try {
        await dispatch(new actions.RequestDeleteScheduleDefinition());
        await api.deleteSchedule(favouriteId, scheduleDefinitionId);
        await dispatch(new actions.ReceiveDeleteScheduleDefinition());
      } catch (err) {
          dispatch(new actions.FailedToDeleteScheduleDefinition());
          dispatch(new ErrorNotification(err));
      }
  },

  getScheduleDefinition: (favouriteId: number): ActionDispatching<any, any> => 
    async (dispatch: any, getState: () => ApplicationState) => {
      try {
        dispatch(new actions.RequestScheduleDefinition());
        const scheduleDefinitions = await api.getScheduleDefinition(favouriteId);
        dispatch(new actions.ReceiveScheduleDefinition(scheduleDefinitions));
      } catch (err) {
        const errorMessage = err?.userMessages || err?.message;
        !!errorMessage && dispatch(new ErrorNotification(errorMessage));
        dispatch(new actions.FailedToReceiveScheduleDefinition());
      }
    },

  updateScheduleDefinition: (
    scheduleDefinition: ScheduleDefinition,
    favouriteId: number,
    includeFailedConstraints: boolean = false
  ): ActionDispatching<any, any> =>
    async (dispatch: any, getState: () => ApplicationState) => {
      try {
        dispatch(new actions.RequestUpdateScheduleDefinition(scheduleDefinition)); 
        const scheduleFailedConstraints = !!scheduleDefinition && await api.saveScheduleDefinition(
          mapScheduleDefinitionToWire(scheduleDefinition),
          favouriteId,
          includeFailedConstraints
          );
        dispatch(new actions.ReceiveUpdateScheduleDefinition(scheduleFailedConstraints));
      } catch (err) {
        const errorMessage = err?.userMessages || err?.message;
        !!errorMessage && dispatch(new ErrorNotification(errorMessage));
        dispatch(new actions.FailedToUpdateScheduleDefinition());
      }
    },

  createScheduleDefinition: (
    favouriteId: number
  ): ActionDispatching<any, any> =>
    async (dispatch: any, getState: () => ApplicationState) => {
      try {
        await dispatch(new actions.RequestDefaultScheduleDefinition()); 
        const result = await api.createScheduleDefinition(favouriteId);
        await dispatch(new actions.ReceiveDefaultScheduleDefinition(result));
      } catch (err) {
        const reportState = getState().companyReport;
        const translate = createDescriptionSelector(reportState.texts);
        const errorMessage = err?.userMessages || err?.message || translate(reportTranslation.save_schedule_failed);
        !!errorMessage && dispatch(new ErrorNotification(errorMessage));
        dispatch(new actions.FailedToReceiveDefaultScheduleDefinition());
      }
    }
};
